import type { ReceiptData, ReceiptPrintOptions } from '@matix/types';
import { escapeHtml } from './format.js';
import { buildZatcaQrSvg } from './zatcaQr.js';
import { amountInWordsAr } from './amountInWords.js';

/**
 * The A4 tax invoice for FlowFresh — their sheet, not ours.
 *
 * FlowFresh hand their customers an invoice laid out quite differently from
 * the `P_S_52_CashSales` sheet the other KSA tenants get (see `buildA4Html`):
 * their branding across the top, a three-column bilingual grid where every
 * English label has its Arabic twin on the same row, and blue/grey banded
 * tables. It is a distinct document, so it is a distinct file — this one is
 * chosen for the FlowFresh tenant only (`buildReceiptHtml`), and nothing here
 * is shared with the other sheet beyond the QR builder.
 *
 *   ┌ logo                         navy banner (menu logo) ┐
 *   │        فاتورة ضريبه مبسطة              ┌────────┐    │
 *   │        Simplified Tax Invoice          │   QR   │    │
 *   ├───────────────┬──────────────┬─────────┴────────┴────┤
 *   │ Invoice #:    │      5       │        :رقم الفاتورة  │
 *   ├───────────────┴──────────────┼───────────────────────┤
 *   │ Seller : / : المورد          │ Buyer: / :العميل      │  ← blue bars
 *   │ Name / Address / City / …    │ Name / Address / …    │
 *   ├──────────────────────────────┴───────────────────────┤
 *   │ Line Items: / :عناصر البند                           │  ← blue bar
 *   │ # │ Nature of services │ Qty │ Price │ … │ Subtotal  │  ← grey head
 *   ├──────────────────────────────────────────────────────┤
 *   │ Total: │ ﷼ 8.70 │ :الاجمالى غير شامل للضريبه         │  ← blue ends
 *   │        عشرة ريال و واحد هللة فقط لا غير               │
 *   └──────────────────────────────────────────────────────┘
 *              ▄▄▄▄▄▄▄▄▄▄ navy wave ▄▄▄▄▄▄▄▄▄▄
 *
 * ── Where the numbers in `CSS` come from ────────────────────────────────────
 *
 * Every length is measured off a rendering of the sheet FlowFresh supplied.
 * That reference is 636 px across; its page edge is cropped on the right, so
 * the scale is anchored on the page rather than on the crop: the sheet is
 * 210 mm across 594 px, i.e. **1 reference px = 0.35354 mm**. Under that
 * anchor the tables come out 198.3 mm wide with a 7.4 mm left margin, and the
 * type lands on ordinary report sizes (body 9.5 pt, invoice block 10.5 pt,
 * title 13.7 pt) — which is the check that the anchor is the right one.
 *
 * Font sizes were not eyeballed: each string's ink width was measured in the
 * reference and the Arial size whose advance matches it solved for, then
 * converted at 1 ref px = 1.3363 CSS px. Do not "tidy" these values.
 *
 * ── The three things that are not cosmetic ──────────────────────────────────
 *
 *   1. **Per-line tax is derived.** The POS prices lines ex-VAT and taxes the
 *      header, so the header discount is allocated pro rata and the last line
 *      absorbs the rounding remainder — that is what makes the Taxable / Tax /
 *      Subtotal columns add up to the boxed totals, which is the first thing
 *      an auditor checks.
 *   2. **The QR is the ERP's, byte for byte** — TLV tags 1-5, a local
 *      `yyyy-MM-ddTHH:mm:ss.fff` timestamp with no zone, 2-decimal amounts.
 *      Shared with `buildA4Html` via `zatcaQr.ts`; do not "fix" it to UTC.
 *   3. **The riyal mark is drawn, not typed.** The reference carries the 2025
 *      Saudi Riyal symbol, which no font on a cashier tablet is guaranteed to
 *      have — U+FDFC (﷼) would print the old "ريال" ligature instead. It is
 *      inline SVG so it prints identically everywhere.
 *
 * The top-right banner is the tenant's **menu logo** (admin → Branding), the
 * top-left mark their receipt logo. Either missing just leaves its corner
 * empty; the rest of the sheet is unaffected.
 */

type CurrencyArg = string | { code: string; precision?: number };

/**
 * The tenants that print this sheet — a hardcoded list, deliberately.
 *
 * This is one tenant's bespoke document, not a feature: there is no admin
 * switch to get it wrong, and a tenant added here is a code change someone
 * reviewed. `tenantDb` comes from the server with the print config (see
 * `printConfig.service.ts`); a client that omits it simply gets the standard
 * sheet, which is the safe direction to fail in.
 */
const FLOWFRESH_TENANT_DBS = ['gl_web_flowfresh'];

/** Does this tenant print FlowFresh's A4 invoice instead of the standard one? */
export function usesFlowFreshA4(pc: { tenantDb?: string | null }): boolean {
  const db = (pc.tenantDb ?? '').trim().toLowerCase();
  return db !== '' && FLOWFRESH_TENANT_DBS.includes(db);
}

/** Every label on the sheet, as the reference spells them (typos included). */
const L = {
  titleAr: 'فاتورة ضريبه مبسطة',
  titleEn: 'Simplified Tax Invoice',
  // A buyer with a VAT number gets a full TAX invoice (the user, 2026-10-14).
  taxTitleAr: 'فاتورة ضريبية',
  taxTitleEn: 'Tax Invoice',

  invoiceNo: { en: 'Invoice #:', ar: 'رقم الفاتورة:' },
  issueDate: { en: 'Invoice Issue Date:', ar: 'تاريخ إصدار الفاتورة:' },
  dueDate: { en: 'Due Date:', ar: 'تاريخ الإستحقاق:' },

  seller: { en: 'Seller :', ar: 'المورد :' },
  buyer: { en: 'Buyer:', ar: 'العميل:' },
  name: { en: 'Name:', ar: 'الإسم:' },
  address: { en: 'Address:', ar: 'العنوان:' },
  city: { en: 'City:', ar: 'مدينة:' },
  country: { en: 'Country:', ar: 'دولة:' },
  zip: { en: 'ZIP/Postal Code:', ar: 'الرمز/الكود البريدى:' },
  phone: { en: 'Phone:', ar: 'هاتف:' },
  vatNumber: { en: 'VAT Number:', ar: 'رقم تسجيل القيمة المضافة:' },
  commercialNo: { en: 'Commercial No:', ar: 'رقم السجل التجارى:' },

  lineItems: { en: 'Line Items:', ar: 'عناصر البند:' },
  colNo: { en: '#', ar: 'رقم' },
  colName: { en: 'Nature of services', ar: 'طبيعة الخدمات' },
  colQty: { en: 'Qty', ar: 'الكمية' },
  colPrice: { en: 'Price', ar: 'السعر' },
  colTaxable: { en: 'Taxable Amount', ar: 'المبلغ الخاضع للضريبة' },
  colTax: { en: 'Tax', ar: 'الضريبة' },
  colSubtotal: { en: 'Subtotal (Incl. VAT)', ar: 'الاجمالي مع الضريبة' },

  total: { en: 'Total:', ar: 'الاجمالى غير شامل للضريبه:' },
  tax: { en: 'Tax:', ar: 'الضريبه:' },
  totalAmount: { en: 'Total Amount:', ar: 'المبلغ الاجمالى:' },
} as const;

interface Label { en: string; ar: string }

function currencyPrecision(currency: CurrencyArg): number {
  return typeof currency === 'string' ? 2 : (currency.precision ?? 2);
}

/** 2100 → "2,100.00". Latin digits with grouping, as the reference prints. */
function num(value: number, precision: number): string {
  const safe = Number.isFinite(value) ? value : 0;
  return safe.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
}

/** Quantities carry three decimals on this sheet ("1.000"). */
function qtyText(qty: number): string {
  const safe = Number.isFinite(qty) ? qty : 0;
  return safe.toLocaleString('en-US', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

/**
 * Components of the receipt's entry date.
 *
 * `entryDate` is a NAIVE wall-clock string in the tenant's timezone. Reading
 * the digits out of the string rather than via `new Date(...)` keeps a tablet
 * whose clock is set to another country from shifting the invoice date — and
 * keeps the QR timestamp identical to the one the ERP writes.
 */
const NAIVE_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,7}))?)?/;

interface DateParts { y: number; mo: number; d: number; h: number; mi: number; s: number; ms: number }

function dateParts(value: string): DateParts | null {
  const m = NAIVE_RE.exec((value ?? '').trim());
  if (m) {
    return {
      y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]),
      h: Number(m[4]), mi: Number(m[5]), s: Number(m[6] ?? 0),
      ms: Number((m[7] ?? '0').padEnd(3, '0').slice(0, 3)),
    };
  }
  const ms = Date.parse((value ?? '').trim());
  if (Number.isNaN(ms)) return null;
  const d = new Date(ms);
  return {
    y: d.getFullYear(), mo: d.getMonth() + 1, d: d.getDate(),
    h: d.getHours(), mi: d.getMinutes(), s: d.getSeconds(), ms: d.getMilliseconds(),
  };
}

const pad = (n: number, len = 2): string => String(n).padStart(len, '0');

/** "11-08-2026" — the issue date, as the reference prints it. */
function invoiceDateText(value: string): string {
  const p = dateParts(value);
  if (!p) return (value ?? '').trim();
  return `${pad(p.d)}-${pad(p.mo)}-${p.y}`;
}

/** The QR's timestamp: local wall clock, milliseconds, NO zone designator. */
function qrTimestamp(value: string): string {
  const p = dateParts(value) ?? { y: 1970, mo: 1, d: 1, h: 0, mi: 0, s: 0, ms: 0 };
  return `${p.y}-${pad(p.mo)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}:${pad(p.s)}.${pad(p.ms, 3)}`;
}

/** One row of the items table, with its share of the discount and its VAT. */
interface PricedLine {
  index: number;
  description: string;
  qty: number;
  unitPrice: number;
  net: number;
  taxRate: number;
  tax: number;
  gross: number;
  isReturn: boolean;
}

/**
 * Derive the per-line tax breakdown.
 *
 * Identical arithmetic to the other A4 sheet: the header discount is allocated
 * pro rata across the lines, VAT is charged on each line's share, and the last
 * line takes the rounding difference on both the net and the VAT column, so
 * Σ taxable, Σ tax and Σ subtotal match the boxed totals to the last halala.
 */
function priceLines(data: ReceiptData, arabic: boolean, precision: number): PricedLine[] {
  const lines = data.items;
  if (lines.length === 0) return [];

  const round = (n: number): number => {
    const f = 10 ** precision;
    return Math.round((n + Number.EPSILON) * f) / f;
  };

  const grossOfLines = lines.reduce((s, l) => s + Math.abs(l.total), 0);
  const discount = Math.max(0, data.totals.discount ?? 0);
  const netTotal = Math.max(0, grossOfLines - discount);

  const rate =
    data.taxPercent != null && data.taxPercent > 0
      ? data.taxPercent
      : netTotal > 0
        ? round((data.totals.tax / netTotal) * 100)
        : 0;

  const out: PricedLine[] = [];
  let netRunning = 0;
  let taxRunning = 0;

  lines.forEach((line, index) => {
    const lineGrossEx = Math.abs(line.total);
    const share = grossOfLines > 0 ? lineGrossEx / grossOfLines : 0;
    const isLast = index === lines.length - 1;

    let net = round(lineGrossEx - discount * share);
    if (isLast) net = round(netTotal - netRunning);
    netRunning = round(netRunning + net);

    let tax = round((net * rate) / 100);
    if (isLast) tax = round((data.totals.tax ?? 0) - taxRunning);
    taxRunning = round(taxRunning + tax);

    // The reference prints ONE name in the services column — no code, no
    // second language, no modifier list. Keep it that way; the tenant reads
    // this sheet as their own document.
    const en = typeof line.name === 'string' ? line.name.trim() : '';
    const ar = typeof line.nameAr === 'string' ? line.nameAr.trim() : '';

    out.push({
      index: index + 1,
      description: (arabic ? ar || en : en || ar) || line.name,
      qty: Math.abs(line.qty),
      unitPrice: Math.abs(line.price),
      net: line.isReturn ? -net : net,
      taxRate: rate,
      tax: line.isReturn ? -tax : tax,
      gross: round(net + tax) * (line.isReturn ? -1 : 1),
      isReturn: Boolean(line.isReturn),
    });
  });

  return out;
}

/*
 * Geometry — millimetres off the reference sheet at 1 px = 0.35354 mm, and
 * font sizes in CSS px at 1 ref px = 1.3363 CSS px. See the file header.
 *
 * Reference landmarks, for anyone re-measuring:
 *   logo box      x  66..278  y  37..132      banner   x 308..586  y 39..132
 *   QR            x 438..518  y 112..192      title    y 133..162
 *   invoice table y 202..254  (3 rows @ 17.4) cols     138.5 / 280.5 / 139
 *   parties bar   y 259..277  rows y 277..411 (8 @ 16.7)
 *   items bar     y 423..439  head y 439..466 rows @ 25, tail row 9
 *   totals        y 500..548  (3 @ 16)        words    y 548..565
 *   wave          x   9..586  y 710..742
 */
const CSS = `
  @page { size: A4 portrait; margin: 0; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0;
    background: #fff; color: #000;
    font-family: Arial, Helvetica, sans-serif;
    line-height: 1.15;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  /* The reference's Arabic is a naskh whose glyphs run about half its em, so
     every Arabic size here looks outsized next to its English twin and is not.
     Devices without it (Android tablets) fall through the list; each fallback
     carries the size-adjust that keeps it the same width, so the sheet does
     not reflow on a different machine. */
  @font-face { font-family: ArTrad;    src: local("Traditional Arabic"); }
  @font-face { font-family: ArSimp;    src: local("Simplified Arabic");  size-adjust: 77%; }
  @font-face { font-family: ArTypeset; src: local("Arabic Typesetting"); size-adjust: 111%; }
  @font-face { font-family: ArMajalla; src: local("Sakkal Majalla");     size-adjust: 84%; }
  @font-face { font-family: ArNaskh;   src: local("Noto Naskh Arabic");  size-adjust: 66%; }
  @font-face { font-family: ArAmiri;   src: local("Amiri");              size-adjust: 76%; }
  @font-face { font-family: ArSegoe;   src: local("Segoe UI");           size-adjust: 57%; }
  .ar {
    font-family: ArTrad, ArSimp, ArTypeset, ArMajalla, ArNaskh, ArAmiri, ArSegoe,
      Tahoma, Arial, sans-serif;
    direction: rtl; unicode-bidi: isolate;
  }
  .num { unicode-bidi: plaintext; white-space: nowrap; }

  /* The page keeps clear of the wave, which is fixed to the foot of every
     printed sheet. Left/right padding is the reference's graphics band
     (x 8..586); the tables sit inside it by their own margins. */
  .sheet { width: 210mm; padding: 5.7mm 2.8mm 22mm 2.8mm; }
  .grid { margin: 0 1.8mm 0 4.6mm; }   /* → tables span x21..581 */

  /* ── Masthead ──
     One mark to each edge of the sheet, as the reference sets them. They only
     read as balanced there because both are sized below rather than left to
     their own resolution; centring them as a pair was a detour around that. */
  .head {
    display: flex; align-items: flex-start; justify-content: space-between;
    height: 26.3mm;
  }
  .head .mark, .head .banner { flex: 0 1 auto; }
  /* These boxes are the ARTWORK's size on the reference sheet, not the image
     file's — which is the whole trick, and why earlier attempts kept landing
     wrong. FlowFresh's receipt logo is a 500×500 canvas whose mark occupies
     only the middle 45% (27.6% dead space above and below, 8.2% each side),
     so a box sized to the canvas prints a mark half the size asked for, and
     one sized to the mark leaves it floating. "cover" scales the canvas by
     width and crops the dead bands away, so the box IS the mark: 49 mm of
     artwork, 21 mm tall, its left edge 22.3 mm in — measured off the sheet.
     A logo re-uploaded with tighter margins would crop instead of float;
     re-measure with scripts/inspect-branding-images.ts if one ever is. */
  .head .mark { margin-left: 14.7mm; }
  .head .mark img { display: block; width: 58.7mm; height: 26.3mm; object-fit: cover; }
  /* The banner file is near full-bleed (512×115, 98% covered), so it needs no
     cropping — just the width its artwork has on the reference, ending 4.5 mm
     from the paper edge. */
  .head .banner { margin-top: 1mm; margin-right: 1mm; }
  .head .banner img { display: block; width: 94.4mm; height: 21.2mm; object-fit: contain; }

  /* The QR hangs 1 mm into the masthead's row, which is what puts it beside
     the title rather than under it. It clears the banner above by 7 mm and the
     invoice table below by 4.5 mm — those two gaps are why the row is deeper
     than the title needs; shortening it would put the code on the banner. */
  .titlerow { position: relative; height: 24mm; }
  .title { width: 172.9mm; text-align: center; padding-top: 7.5mm; }
  .title .t-ar { font-size: 26px; font-weight: 700; line-height: 1; }
  .title .t-en { font-size: 18.3px; font-weight: 700; line-height: 1.15; margin-top: 0.7mm; }
  .qr { position: absolute; left: 147.4mm; top: 1.8mm; width: 28.6mm; }
  .qr svg { display: block; width: 28.6mm; height: 28.6mm; }

  /* ── Shared table furniture ── */
  table { border-collapse: collapse; table-layout: fixed; width: 100%; }
  td, th { padding: 0 1.2mm; vertical-align: middle; }
  .rule td, .rule th { border: 0.35mm solid #d3d3d3; }
  .bar {
    background: #4688c3; color: #fff; font-weight: 700;
    border: 0.35mm solid #4688c3;
  }
  .bar .l { float: left; }
  .bar .r { float: right; }

  /* ── Invoice number / dates ── */
  table.inv { margin-top: 8.1mm; }
  table.inv td { height: 6.15mm; border: 0.35mm solid #d3d3d3; font-size: 14px; }
  table.inv td.k-en { width: 24.7%; font-weight: 700; }
  table.inv td.v { width: 50%; text-align: center; }
  table.inv td.k-ar { width: 25.3%; text-align: right; font-size: 22px; font-weight: 700; }

  /* ── Seller / buyer ──
     Two independent tables side by side, 3.5 ref px apart, exactly as the
     reference sets them; the buyer table is a shade narrower. */
  .parties { display: flex; justify-content: space-between; margin-top: 1.8mm; }
  .parties .party { width: 50.1%; }
  .parties .party + .party { width: 49.2%; }
  table.party td { height: 5.9mm; font-size: 12.7px; border: 0.35mm solid #d3d3d3; }
  table.party td.k-en { width: 30.8%; font-weight: 700; }
  table.party td.v { width: 34.5%; text-align: right; }
  table.party td.v.ar { text-align: right; font-size: 17px; }
  table.party td.k-ar { width: 34.7%; text-align: right; font-size: 19px; }
  table.party td.bar { height: 6.4mm; font-size: 12.4px; }
  table.party td.bar .r { font-size: 19px; }

  /* ── Items ── */
  table.lines { margin-top: 4.4mm; }
  table.lines td.bar, table.lines th.bar { height: 5.7mm; font-size: 12.4px; text-align: left; }
  table.lines th.bar .r { font-size: 19px; }
  table.lines thead th.col {
    height: 9.4mm; background: #737373; color: #fff; font-weight: 700;
    text-align: center; border: 0.35mm solid #737373; font-size: 13px;
    line-height: 1.1; white-space: nowrap;
  }
  table.lines thead th.col .h-ar { font-size: 19px; font-weight: 400; line-height: 1; }
  table.lines tbody td {
    height: 8.8mm; text-align: center; font-size: 12.3px;
    border: 0.35mm solid #d3d3d3;
  }
  table.lines tbody td.name { font-size: 12.3px; }
  table.lines tbody td.name.ar { font-size: 17px; }
  table.lines tbody tr.ret td { color: #8a1c1c; }
  table.lines tbody td.tail { height: 3.2mm; }
  table.lines tr { break-inside: avoid; page-break-inside: avoid; }
  /* The column heads repeat on a second sheet; nothing else does. */
  table.lines thead { display: table-header-group; }

  /* ── Totals ── */
  table.totals td { height: 5.7mm; border: 0.35mm solid #d3d3d3; }
  table.totals td.k-en {
    width: 25%; background: #4688c3; color: #fff; font-weight: 700; font-size: 12.6px;
    border-color: #4688c3;
  }
  table.totals td.v {
    width: 50.3%; text-align: center; padding-right: 18mm;
    font-size: 13.7px; font-weight: 700;
  }
  table.totals td.k-ar {
    width: 24.7%; background: #4688c3; color: #fff; text-align: right; font-size: 19px;
    border-color: #4688c3;
  }
  table.totals td.words { height: 5.8mm; text-align: center; font-size: 19px; }
  .sar { display: inline-block; width: 2.9mm; height: 2.9mm; margin-right: 1.2mm; vertical-align: -0.3mm; }
  .sar svg { display: block; width: 100%; height: 100%; }

  /* The wave is the sheet's footer: fixed, so it lands on every printed page. */
  .wave { position: fixed; left: 3.2mm; bottom: 5mm; width: 204mm; height: 11.7mm; }
  .wave svg { display: block; width: 100%; height: 100%; }
`;

/**
 * The 2025 Saudi Riyal mark: two stems crossed by a full-width bar, a shorter
 * bar below and the right stem's tail. Drawn rather than typed — see the file
 * header. Traced from the reference's glyph.
 */
function sarMark(): string {
  return (
    `<svg viewBox="0 0 100 84" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">` +
    `<path fill="currentColor" d="` +
    // left stem
    `M30 4 h13 v52 h-13 z ` +
    // right stem, with the tail that falls away to the right
    `M55 4 h13 v40 h-13 z ` +
    `M68 44 h13 l-8 30 h-13 z ` +
    // upper crossbar (full width) and the shorter lower bar
    `M2 40 h96 v10 h-96 z ` +
    `M2 60 h55 v10 h-55 z` +
    `"/></svg>`
  );
}

/**
 * The navy/blue wave the sheet ends on. It is FlowFresh's footer graphic, and
 * it is geometry rather than a photo, so it is drawn: an image would have to
 * be carried as a 200 kB data URL through every print.
 */
function waveSvg(): string {
  return (
    `<svg viewBox="0 0 577 33" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">` +
    `<rect x="0" y="10" width="577" height="17" fill="#2e2d6f"/>` +
    `<path fill="#1c2a63" d="M243 33 L281 0 h34 l-38 33 z"/>` +
    `<path fill="#1c2a63" d="M301 33 L339 0 h34 l-38 33 z"/>` +
    `<path fill="#23baeb" d="M258 33 L296 0 h30 l-38 33 z"/>` +
    `<path fill="#23baeb" d="M316 33 L354 0 h30 l-38 33 z"/>` +
    `</svg>`
  );
}

/** One label row of a party card: English label, value, Arabic label. */
function partyRow(l: Label, value: string, arabicValue: boolean): string {
  const v = value.trim();
  const cls = arabicValue && v ? 'v ar' : 'v num';
  return (
    `<tr><td class="k-en">${escapeHtml(l.en)}</td>` +
    `<td class="${cls}">${escapeHtml(v)}</td>` +
    `<td class="k-ar ar">${escapeHtml(l.ar)}</td></tr>`
  );
}

/** A party card: the blue bar, then its eight rows. */
function partyCard(l: Label, rows: string): string {
  return (
    `<table class="party">` +
    `<tr><td class="bar" colspan="3">` +
    `<span class="l">${escapeHtml(l.en)}</span>` +
    `<span class="r ar">${escapeHtml(l.ar)}</span>` +
    `</td></tr>${rows}</table>`
  );
}

/** A column head: the English line over its smaller Arabic one. */
function colHead(l: Label): string {
  return (
    `<th class="col"><div>${escapeHtml(l.en)}</div>` +
    `<div class="h-ar ar">${escapeHtml(l.ar)}</div></th>`
  );
}

/**
 * Compose FlowFresh's A4 tax invoice.
 *
 * `arabic` decides only which language leads the item name; every label on the
 * sheet prints in both, always, because that is the paper their customers get.
 */
export function buildA4FlowFreshHtml(
  data: ReceiptData,
  currency: CurrencyArg,
  _po: ReceiptPrintOptions,
  arabic: boolean,
): string {
  const pc = data.printConfig;
  const precision = currencyPrecision(currency);
  const money = (n: number): string => num(n, precision);

  const lines = priceLines(data, arabic, precision);

  // Totals. "Total:" is the taxable base — subtotal less the header discount —
  // so it equals Σ of the Taxable Amount column above it.
  const subjectToVat = Math.max(0, data.totals.subtotal ?? 0);
  const discount = Math.max(0, data.totals.discount ?? 0);
  const taxable = subjectToVat - discount;
  const vatTotal = data.totals.tax ?? 0;
  const grossTotal = data.totals.total ?? taxable + vatTotal;

  // ── Seller ──
  const sellerName = (pc.companyNameAr ?? pc.companyNameEn ?? '').trim();
  const sellerVat = (pc.taxRegisterNo ?? '').trim();
  // The ERP keeps the commercial registration in mstr_TaxSetting.TaxCode, not
  // in the column named for it — which stays null on these tenants. Prefer the
  // one their own invoice prints, and fall back to register_no for a tenant
  // that fills it in.
  const sellerCr = (pc.commercialNo ?? pc.registerNo ?? '').trim();
  const sellerAddress =
    (pc.branchAddressLine ?? pc.companyAddressAr ?? pc.companyAddressEn ?? '').trim();
  const sellerPhone = (pc.companyContactAr ?? pc.companyContactEn ?? '').trim();

  // ── Buyer ──
  const buyerName = (data.customer?.nameAr ?? data.customer?.name ?? '').trim();
  const buyerPhone = (data.customer?.phone ?? '').trim();
  const buyerVat = (data.customer?.taxNumber ?? '').trim();
  const titleAr = buyerVat ? L.taxTitleAr : L.titleAr;
  const titleEn = buyerVat ? L.taxTitleEn : L.titleEn;
  const buyerAddress = (data.customer?.address ?? '').trim();

  const qrSvg = buildZatcaQrSvg({
    sellerName,
    vatNumber: sellerVat,
    timestamp: qrTimestamp(data.entryDate),
    total: grossTotal.toFixed(2),
    vat: vatTotal.toFixed(2),
    sizeMm: 28.6,
  });

  const sellerRows =
    partyRow(L.name, sellerName, true) +
    partyRow(L.address, sellerAddress, true) +
    partyRow(L.city, '', true) +
    partyRow(L.country, '', true) +
    partyRow(L.zip, '', false) +
    partyRow(L.phone, sellerPhone, false) +
    partyRow(L.vatNumber, sellerVat, false) +
    partyRow(L.commercialNo, sellerCr, false);

  const buyerRows =
    partyRow(L.name, buyerName, true) +
    partyRow(L.address, buyerAddress, true) +
    partyRow(L.city, '', true) +
    partyRow(L.country, '', true) +
    partyRow(L.zip, '', false) +
    partyRow(L.phone, buyerPhone, false) +
    partyRow(L.vatNumber, buyerVat, false) +
    partyRow(L.commercialNo, '', false);

  const itemRows = lines
    .map(
      (l) =>
        `<tr${l.isReturn ? ' class="ret"' : ''}>` +
        `<td class="num">${escapeHtml(String(l.index))}</td>` +
        `<td class="name${arabic ? ' ar' : ''}">${escapeHtml(l.description)}</td>` +
        `<td class="num">${escapeHtml(qtyText(l.qty))}</td>` +
        `<td class="num">${escapeHtml(money(l.unitPrice))}</td>` +
        `<td class="num">${escapeHtml(money(l.net))}</td>` +
        `<td class="num">${escapeHtml(money(l.tax))}<br>${escapeHtml(l.taxRate.toFixed(2))}%</td>` +
        `<td class="num">${escapeHtml(money(l.gross))}</td>` +
        `</tr>`,
    )
    .join('\n');

  const mark = sarMark();
  const totalRow = (l: Label, value: number): string =>
    `<tr><td class="k-en">${escapeHtml(l.en)}</td>` +
    `<td class="v"><span class="sar">${mark}</span><span class="num">${escapeHtml(money(value))}</span></td>` +
    `<td class="k-ar ar">${escapeHtml(l.ar)}</td></tr>`;

  const numberForTitle = String(data.orderCode ?? data.orderId);

  return `<!DOCTYPE html>
<html lang="en" dir="ltr">
<head>
<meta charset="utf-8">
<title>${escapeHtml(titleEn)} #${escapeHtml(numberForTitle)}</title>
<style>${CSS}</style>
</head>
<body>
<div class="sheet">

<div class="head">
  <div class="mark">${pc.receiptLogo ? `<img src="${escapeHtml(pc.receiptLogo)}" alt="">` : ''}</div>
  <div class="banner">${pc.menuLogo ? `<img src="${escapeHtml(pc.menuLogo)}" alt="">` : ''}</div>
</div>

<div class="titlerow">
  <div class="title">
    <div class="t-ar ar">${escapeHtml(titleAr)}</div>
    <div class="t-en">${escapeHtml(titleEn)}</div>
  </div>
  <div class="qr">${qrSvg}</div>
</div>

<div class="grid">
<table class="inv">
  <tr>
    <td class="k-en">${escapeHtml(L.invoiceNo.en)}</td>
    <td class="v num">${escapeHtml(numberForTitle)}</td>
    <td class="k-ar ar">${escapeHtml(L.invoiceNo.ar)}</td>
  </tr>
  <tr>
    <td class="k-en">${escapeHtml(L.issueDate.en)}</td>
    <td class="v num">${escapeHtml(invoiceDateText(data.entryDate))}</td>
    <td class="k-ar ar">${escapeHtml(L.issueDate.ar)}</td>
  </tr>
  <tr>
    <td class="k-en">${escapeHtml(L.dueDate.en)}</td>
    <td class="v num"></td>
    <td class="k-ar ar">${escapeHtml(L.dueDate.ar)}</td>
  </tr>
</table>

<div class="parties">
  ${partyCard(L.seller, sellerRows)}
  ${partyCard(L.buyer, buyerRows)}
</div>

<table class="lines">
  <colgroup>
    <col style="width:5.3%"><col style="width:28.7%"><col style="width:9.8%">
    <col style="width:10%"><col style="width:16.5%"><col style="width:11%">
    <col style="width:18.7%">
  </colgroup>
  <thead>
    <tr><th class="bar" colspan="7">
      <span class="l">${escapeHtml(L.lineItems.en)}</span>
      <span class="r ar">${escapeHtml(L.lineItems.ar)}</span>
    </th></tr>
    <tr>
      ${colHead(L.colNo)}${colHead(L.colName)}${colHead(L.colQty)}${colHead(L.colPrice)}
      ${colHead(L.colTaxable)}${colHead(L.colTax)}${colHead(L.colSubtotal)}
    </tr>
  </thead>
  <tbody>
${itemRows}
    <tr><td class="tail" colspan="7"></td></tr>
  </tbody>
</table>

<table class="totals">
${totalRow(L.total, taxable)}
${totalRow(L.tax, vatTotal)}
${totalRow(L.totalAmount, grossTotal)}
  <tr><td class="words ar" colspan="3">${escapeHtml(amountInWordsAr(grossTotal, precision))}</td></tr>
</table>
</div>

</div>
<div class="wave">${waveSvg()}</div>
</body>
</html>`;
}
